import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react'
import { Link } from 'react-router-dom'
import { getPlatformStats, type PlatformStats, type PlatformTenantRow } from '@/api/platformApi'
import { usePlatformTenantsList } from '@/pages/Platform/usePlatformTenantsList'
import { getPlatformTenantsPath, getPlatformTenantDetailPath } from '@/config/platform'
import { PlatformExportActions } from '@/pages/Platform/PlatformExportActions'
import styles from './Platform.module.css'

const PlatformCharts = lazy(() =>
  import('./PlatformCharts').then((m) => ({ default: m.PlatformCharts }))
)

function sum(rows: PlatformTenantRow[], key: keyof PlatformTenantRow): number {
  return rows.reduce((acc, r) => acc + Number(r[key] ?? 0), 0)
}

function formatPtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

function trendFromPeriods(cur: number, prev: number): { label: string; cls: string } {
  if (cur === 0 && prev === 0) return { label: 'Sem agendamentos nestes períodos', cls: styles.trendFlat }
  if (prev === 0 && cur > 0) return { label: '↑ Volume nos últimos 30 dias', cls: styles.trendUp }
  const pct = Math.round(((cur - prev) / prev) * 100)
  if (pct > 0) return { label: `↑ ${pct}% vs. 30 dias anteriores`, cls: styles.trendUp }
  if (pct < 0) return { label: `↓ ${Math.abs(pct)}% vs. 30 dias anteriores`, cls: styles.trendDown }
  return { label: '→ Estável vs. período anterior', cls: styles.trendFlat }
}

type KpiDef = {
  key: string
  label: string
  icon: string
  value: string
  hint: string
  variant?: 'accent' | 'violet' | 'amber' | 'emerald' | 'rose'
  trend?: { label: string; cls: string }
}

const KPI_VARIANT: Record<string, string> = {
  accent: styles.kpiCardAccent,
  violet: styles.kpiCardViolet,
  amber: styles.kpiCardAmber,
  emerald: styles.kpiCardEmerald,
  rose: styles.kpiCardRose,
}

const KPI_SM_VARIANT: Record<string, string> = {
  accent: styles.kpiCardSmAccent,
  violet: styles.kpiCardSmViolet,
  amber: styles.kpiCardSmAmber,
  emerald: styles.kpiCardSmEmerald,
  rose: styles.kpiCardSmRose,
}

export function PlatformOverview() {
  const { rows, loading, erro, reload } = usePlatformTenantsList()
  const [apiStats, setApiStats] = useState<PlatformStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const [statsErro, setStatsErro] = useState('')
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)

  const loadStats = useCallback(() => {
    setStatsLoading(true)
    setStatsErro('')
    getPlatformStats()
      .then((data) => {
        setApiStats(data)
        setLastRefresh(new Date())
      })
      .catch((e) => setStatsErro(e instanceof Error ? e.message : 'Erro nas estatísticas.'))
      .finally(() => setStatsLoading(false))
  }, [])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  const refreshAll = () => {
    reload()
    loadStats()
  }

  const stats = useMemo(() => {
    const n = rows.length
    const ativas = rows.filter((r) => r.ativo).length
    const suspensas = n - ativas
    const agendamentos = sum(rows, 'agendamentos_total')
    const usuarios = sum(rows, 'usuarios_total')
    const profissionais = sum(rows, 'profissionais_total')
    const unidades = sum(rows, 'unidades_total')
    const admins = sum(rows, 'admins_ativos')
    const agMesAtual = sum(rows, 'agendamentos_mes_atual')
    const mediaAgPorConta = n > 0 ? Math.round(agendamentos / n) : 0
    const topAg = [...rows].sort((a, b) => b.agendamentos_total - a.agendamentos_total).slice(0, 6)
    const recentes = [...rows]
      .sort((a, b) => new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime())
      .slice(0, 6)
    const maxAg = topAg[0]?.agendamentos_total ?? 1
    const quotaAlerts = rows.filter(
      (r) => r.quota_usuarios_excedida || r.quota_agendamentos_mes_excedida
    )
    const inactiveLong = rows.filter((r) => r.dias_sem_actividade >= 30).length
    const withBookingsMonth = rows.filter((r) => r.agendamentos_mes_atual > 0).length
    return {
      n,
      ativas,
      suspensas,
      agendamentos,
      usuarios,
      profissionais,
      unidades,
      admins,
      agMesAtual,
      mediaAgPorConta,
      topAg,
      recentes,
      maxAg: Math.max(maxAg, 1),
      pctAtivas: n > 0 ? Math.round((ativas / n) * 100) : 0,
      quotaAlerts,
      inactiveLong,
      withBookingsMonth,
    }
  }, [rows])

  const t = apiStats?.totals
  const trend30 = apiStats
    ? trendFromPeriods(apiStats.agendamentosUltimos30d, apiStats.agendamentosPrev30d)
    : null
  const mediaUsersTenant =
    t && t.tenants_total > 0 ? Math.round(t.usuarios_total / t.tenants_total) : 0

  const primaryKpis = useMemo((): KpiDef[] => {
    const tenantsTotal = t?.tenants_total ?? stats.n
    const agTotal = t?.agendamentos_total ?? stats.agendamentos
    const usersTotal = t?.usuarios_total ?? stats.usuarios
    const prosTotal = t?.profissionais_total ?? stats.profissionais
    return [
      {
        key: 'tenants',
        label: 'Contas',
        icon: '◎',
        value: tenantsTotal.toLocaleString('pt-PT'),
        hint: `${t?.tenants_ativos ?? stats.ativas} ativas · ${t?.tenants_suspensos ?? stats.suspensas} suspensas`,
        variant: 'accent',
      },
      {
        key: 'agendamentos',
        label: 'Agendamentos',
        icon: '◷',
        value: agTotal.toLocaleString('pt-PT'),
        hint: `Média ${
          t && t.tenants_total > 0 ? Math.round(t.agendamentos_total / t.tenants_total) : stats.mediaAgPorConta
        } por conta`,
      },
      {
        key: 'usuarios',
        label: 'Utilizadores',
        icon: '◉',
        value: usersTotal.toLocaleString('pt-PT'),
        hint: `~${mediaUsersTenant || '—'} por conta · ${prosTotal.toLocaleString('pt-PT')} profissionais`,
        variant: 'violet',
      },
      {
        key: 'infra',
        label: 'Infraestrutura',
        icon: '▣',
        value: (t?.unidades_total ?? stats.unidades).toLocaleString('pt-PT'),
        hint: `unidades · ${(t?.admins_ativos ?? stats.admins).toLocaleString('pt-PT')} admins activos`,
        variant: 'amber',
      },
    ]
  }, [t, stats, mediaUsersTenant])

  const secondaryKpis = useMemo((): KpiDef[] => {
    return [
      {
        key: 'ag30',
        label: 'Agendamentos (30 dias)',
        icon: '↗',
        value: apiStats ? apiStats.agendamentosUltimos30d.toLocaleString('pt-PT') : '—',
        hint: 'Volume recente na plataforma',
        trend: trend30 ?? undefined,
      },
      {
        key: 'agmes',
        label: 'Agendamentos (mês)',
        icon: '◈',
        value: stats.agMesAtual.toLocaleString('pt-PT'),
        hint: `${stats.withBookingsMonth} de ${stats.n} contas com marcações`,
        variant: 'emerald',
      },
      {
        key: 'nao-cancel',
        label: 'Não cancelados',
        icon: '✓',
        value: apiStats ? apiStats.agendamentosNaoCancelados.toLocaleString('pt-PT') : '—',
        hint: 'Exclui estado cancelado',
      },
      {
        key: 'feedbacks',
        label: 'Feedbacks',
        icon: '★',
        value: apiStats?.feedbacksTotal != null ? apiStats.feedbacksTotal.toLocaleString('pt-PT') : '—',
        hint: 'Testemunhos na base',
      },
      {
        key: 'compras',
        label: 'Compras',
        icon: '◆',
        value: apiStats?.comprasTotal != null ? apiStats.comprasTotal.toLocaleString('pt-PT') : '—',
        hint: 'Registos de compras',
      },
      {
        key: 'inactivas',
        label: 'Sem actividade',
        icon: '○',
        value: stats.inactiveLong.toLocaleString('pt-PT'),
        hint: 'Contas há 30+ dias sem agendamento',
        variant: stats.inactiveLong > 0 ? 'rose' : undefined,
      },
    ]
  }, [apiStats, stats, trend30])

  const refreshLabel = lastRefresh
    ? lastRefresh.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
    : null

  return (
    <div className={styles.platPage}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Visão geral</h1>
          <p className={styles.pageLead}>
            Painel executivo da plataforma — métricas, tendências e saúde das contas em tempo real.
          </p>
        </div>
        <div className={styles.pageHeaderActions}>
          <PlatformExportActions tenants={rows} stats={apiStats} disabled={loading || statsLoading} />
          <button
            type="button"
            className={styles.btnOutline}
            onClick={() => refreshAll()}
            disabled={loading || statsLoading}
          >
            {loading || statsLoading ? 'A atualizar…' : 'Atualizar tudo'}
          </button>
        </div>
      </div>

      {(erro || statsErro) && (
        <p className={styles.erro} role="alert">
          {[erro, statsErro].filter(Boolean).join(' ')}
        </p>
      )}

      {loading && !rows.length ? (
        <p className={styles.pageLead}>A carregar…</p>
      ) : (
        <>
          <section className={styles.heroBanner} aria-label="Resumo da plataforma">
            <div className={styles.heroGlow} aria-hidden />
            <div className={styles.heroInner}>
              <div className={styles.heroText}>
                <span className={styles.heroEyebrow}>Plataforma SaaS</span>
                <h2 className={styles.heroTitle}>
                  {(t?.tenants_ativos ?? stats.ativas).toLocaleString('pt-PT')} contas activas
                </h2>
                <p className={styles.heroSub}>
                  {(t?.agendamentos_total ?? stats.agendamentos).toLocaleString('pt-PT')} agendamentos totais ·{' '}
                  {(t?.usuarios_total ?? stats.usuarios).toLocaleString('pt-PT')} utilizadores na base
                </p>
                {refreshLabel && (
                  <p className={styles.heroMeta}>Última actualização às {refreshLabel}</p>
                )}
              </div>
              <div className={styles.heroPills}>
                <div className={styles.heroPill}>
                  <span className={styles.heroPillValue}>
                    {apiStats ? apiStats.agendamentosUltimos30d.toLocaleString('pt-PT') : '—'}
                  </span>
                  <span className={styles.heroPillLabel}>Ag. 30 dias</span>
                </div>
                <div className={styles.heroPill}>
                  <span className={styles.heroPillValue}>{stats.agMesAtual.toLocaleString('pt-PT')}</span>
                  <span className={styles.heroPillLabel}>Ag. este mês</span>
                </div>
                <div className={styles.heroPill}>
                  <span className={styles.heroPillValue}>{stats.pctAtivas}%</span>
                  <span className={styles.heroPillLabel}>Contas activas</span>
                </div>
                <div className={styles.heroPill}>
                  <span className={styles.heroPillValue}>
                    {(t?.profissionais_total ?? stats.profissionais).toLocaleString('pt-PT')}
                  </span>
                  <span className={styles.heroPillLabel}>Profissionais</span>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.kpiRowPrimary} aria-label="Indicadores principais">
            {primaryKpis.map((kpi) => (
              <article
                key={kpi.key}
                className={`${styles.kpiCard} ${kpi.variant ? KPI_VARIANT[kpi.variant] ?? '' : ''}`}
              >
                <span className={styles.kpiIcon} aria-hidden>
                  {kpi.icon}
                </span>
                <div className={styles.kpiBody}>
                  <span className={styles.kpiLabel}>{kpi.label}</span>
                  <strong className={styles.kpiValue}>{kpi.value}</strong>
                  <span className={styles.kpiHint}>{kpi.hint}</span>
                </div>
              </article>
            ))}
          </section>

          {statsLoading && !apiStats ? (
            <div className={styles.chartSkeleton} aria-hidden />
          ) : apiStats ? (
            <Suspense fallback={<div className={styles.chartSkeleton} aria-hidden />}>
              <PlatformCharts stats={apiStats} tenants={rows} />
            </Suspense>
          ) : null}

          <section className={styles.kpiRowSecondary} aria-label="Indicadores complementares">
            {secondaryKpis.map((kpi) => (
              <article
                key={kpi.key}
                className={`${styles.kpiCardSm} ${kpi.variant ? KPI_SM_VARIANT[kpi.variant] ?? '' : ''}`}
              >
                <span className={styles.kpiIconSm} aria-hidden>
                  {kpi.icon}
                </span>
                <div className={styles.kpiBody}>
                  <span className={styles.kpiLabelSm}>{kpi.label}</span>
                  <strong className={styles.kpiValueSm}>{kpi.value}</strong>
                  {kpi.trend ? (
                    <span className={`${styles.trendChip} ${kpi.trend.cls}`}>{kpi.trend.label}</span>
                  ) : (
                    <span className={styles.kpiHintSm}>{kpi.hint}</span>
                  )}
                </div>
              </article>
            ))}
          </section>

          {stats.quotaAlerts.length > 0 && (
            <section className={styles.alertBanner} role="alert">
              <div className={styles.alertBannerIcon} aria-hidden>
                !
              </div>
              <div>
                <strong className={styles.alertBannerTitle}>
                  {stats.quotaAlerts.length} conta{stats.quotaAlerts.length !== 1 ? 's' : ''} com quota excedida
                </strong>
                <p className={styles.alertBannerText}>
                  {stats.quotaAlerts
                    .slice(0, 3)
                    .map((r) => r.nome || r.slug)
                    .join(' · ')}
                  {stats.quotaAlerts.length > 3 ? ` · +${stats.quotaAlerts.length - 3}` : ''}
                </p>
              </div>
              <Link className={styles.alertBannerLink} to={getPlatformTenantsPath()}>
                Ver contas →
              </Link>
            </section>
          )}

          <div className={styles.insightGrid}>
            <article className={styles.insightCard}>
              <span className={styles.insightIcon} aria-hidden>
                ◎
              </span>
              <div>
                <strong className={styles.insightValue}>{stats.pctAtivas}%</strong>
                <p className={styles.insightLabel}>Taxa de contas activas</p>
                <p className={styles.insightSub}>
                  {stats.ativas} de {stats.n} empresas operacionais
                </p>
              </div>
            </article>
            <article className={styles.insightCard}>
              <span className={styles.insightIcon} aria-hidden>
                ◷
              </span>
              <div>
                <strong className={styles.insightValue}>
                  {stats.n > 0 ? Math.round((stats.withBookingsMonth / stats.n) * 100) : 0}%
                </strong>
                <p className={styles.insightLabel}>Contas com agendamentos no mês</p>
                <p className={styles.insightSub}>
                  {stats.withBookingsMonth} de {stats.n} com actividade recente
                </p>
              </div>
            </article>
            <article className={styles.insightCard}>
              <span className={styles.insightIcon} aria-hidden>
                ◉
              </span>
              <div>
                <strong className={styles.insightValue}>{mediaUsersTenant || '—'}</strong>
                <p className={styles.insightLabel}>Média de utilizadores por conta</p>
                <p className={styles.insightSub}>
                  {(t?.usuarios_total ?? stats.usuarios).toLocaleString('pt-PT')} utilizadores no total
                </p>
              </div>
            </article>
            <article className={styles.insightCard}>
              <span className={styles.insightIcon} aria-hidden>
                ▣
              </span>
              <div>
                <strong className={styles.insightValue}>
                  {stats.n > 0 ? (stats.unidades / stats.n).toFixed(1) : '—'}
                </strong>
                <p className={styles.insightLabel}>Unidades por conta (média)</p>
                <p className={styles.insightSub}>
                  {stats.unidades.toLocaleString('pt-PT')} unidades na plataforma
                </p>
              </div>
            </article>
          </div>

          <section className={styles.panel} aria-labelledby="plat-recent-title">
            <div className={styles.panelHead}>
              <h2 id="plat-recent-title" className={styles.panelTitle}>
                Contas mais recentes
              </h2>
              <Link className={styles.panelLink} to={getPlatformTenantsPath()}>
                Gerir todas →
              </Link>
            </div>
            {stats.recentes.length === 0 ? (
              <p className={styles.panelEmpty}>Ainda não há contas.</p>
            ) : (
              <div className={styles.tenantCardGrid}>
                {stats.recentes.map((r) => (
                  <Link
                    key={r.id}
                    to={getPlatformTenantDetailPath(r.id)}
                    className={`${styles.tenantCard} ${!r.ativo ? styles.tenantCardOff : ''}`}
                  >
                    <div className={styles.tenantCardTop}>
                      <code className={styles.tenantCardSlug}>{r.slug}</code>
                      {r.ativo ? (
                        <span className={styles.badgeOn}>Ativa</span>
                      ) : (
                        <span className={styles.badgeOff}>Suspensa</span>
                      )}
                    </div>
                    <p className={styles.tenantCardName}>{r.nome || r.slug}</p>
                    <p className={styles.tenantCardMeta}>Criada {formatPtDate(r.criado_em)}</p>
                    <div className={styles.tenantCardStats}>
                      <span>{r.agendamentos_total} ag.</span>
                      <span>{r.usuarios_total} users</span>
                      <span>{r.agendamentos_mes_atual} este mês</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
